import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConstructionSiteSelectionTableModel } from 'src/app/models/tables/archive/construction-site-selection-table.model';
import { ConstructionSiteService } from 'src/app/services/archive/construction-site.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { DeactivateConstructionSiteErrorsModalComponent } from '../deactivate-construction-site-errors-modal/deactivate-construction-site-errors-modal.component';
import { PreInvoicingStateEnums } from 'src/app/enum/pre-invoicing-state-enums';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-deactivate-construction-site',
  templateUrl: './deactivate-construction-site.component.html',
  styleUrls: ['./deactivate-construction-site.component.scss'],
  standalone: false
})
export class DeactivateConstructionSiteComponent implements OnInit {

  @ViewChild('errorsModal') errorsModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  clearSelection: number = 0;
  columnSort: string | null = null;
  componentErrorsModal = DeactivateConstructionSiteErrorsModalComponent;
  constructionSiteData: PagedResponseModel<ConstructionSiteSelectionTableModel> =
    new PagedResponseModel<ConstructionSiteSelectionTableModel>();
  constructionSiteHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  inputsForErrorsModal$ = new BehaviorSubject<any>({});
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Inactivar Obras';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Inactivar',
      icon: 'pi pi-ban',
      color: ActionColor.DANGER,
      action: 'deactivate'
    },
  ];
  url: string = 'deactivate-construction-site';

  constructor(private constructionSiteService: ConstructionSiteService,
    private confirmDialogService: ConfirmDialogService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private headerService: HeaderService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForSelection(),
      this.getConstructionSitesForSelection(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async deactivateConstructionSites(selectedIds: number[]) {
    if (selectedIds.length > 0) {
      if (await this.confirmDialogService.confirm('¿Realmente desea inactivar las obras seleccionadas?')) {
        try {
          const response = await firstValueFrom(this.constructionSiteService.deactivateConstructionSites(selectedIds));
          const preInvoicingStateId = response?.preInvoicingStateId || 0;
          const alerts = response?.alerts || [];

          if (preInvoicingStateId === PreInvoicingStateEnums.error) {
            this.inputsForErrorsModal$.next({
              invoiceAlerts: alerts,
              errorInvoice: preInvoicingStateId
            });
            this.errorsModalComponent?.show();
            if (preInvoicingStateId === PreInvoicingStateEnums.error) {
              this.toastHandlerService.showToast('error', 'El proceso terminó con errores, puede exportarlos si lo desea');
            } else {
              this.toastHandlerService.showToast('error', 'El proceso terminó con alertas, puede exportarlas si lo desea');
            }
          } else {
            this.toastHandlerService.showToast('success', 'Las obras fueron inactivadas exitosamente.');
            await this.getConstructionSitesForSelection();
          }
        } catch (error) {
          console.error('Error al inactivar las obras.', error);
        }
      }
      await this.getConstructionSitesForSelection();
      this.clearSelection++;
    }
  }

  async getConstructionSitesForSelection() {
    try {
      const response = await firstValueFrom(this.constructionSiteService.getConstructionSitesForSelection(this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
      this.constructionSiteData = response;
    } catch (error) {
      console.error('Error al buscar las obras:', error);
    }
  }

  async getColumnsForSelection() {
    try {
      if (this.constructionSiteHeader.length === 0) {
        const response = await firstValueFrom(this.constructionSiteService.getColumnsForSelection());
        this.constructionSiteHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de obras.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const url = this.router.url as string
    this.url = url.substring(1);
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al obtener los permisos de la ventana.', error);
    }
  }

  async onAction(event: { action: string, item?: ConstructionSiteSelectionTableModel, selectedItems?: number[] }) {
    if (event.action === 'deactivate' && event.selectedItems) {
      await this.deactivateConstructionSites(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getConstructionSitesForSelection();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
